import React, { createContext, useContext, useState, ReactNode } from 'react';
import { ThemeProvider, createTheme, Theme } from '@mui/material/styles';

interface ColorModeContextType {
    toggleColorMode: () => void;
    mode: 'light' | 'dark';
}

const ColorModeContext = createContext<ColorModeContextType>({
    toggleColorMode: () => { },
    mode: 'dark',
});

export const useColorMode = () => useContext(ColorModeContext);

interface ColorModeProviderProps {
    children: ReactNode;
}

export const ColorModeProvider: React.FC<ColorModeProviderProps> = ({ children }) => {
    const [mode, setMode] = useState<'light' | 'dark'>('dark');

    const toggleColorMode = () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
    };

    const theme = createTheme({
        palette: {
            mode,
            primary: {
                main: mode === 'dark' ? '#d4af37' : '#8b6f2e', // Elegant gold
                light: mode === 'dark' ? '#ffd95a' : '#c09853',
                dark: mode === 'dark' ? '#b8941f' : '#5d4b1e',
                contrastText: mode === 'dark' ? '#1a1a1a' : '#ffffff',
            },
            secondary: {
                main: mode === 'dark' ? '#e8dcc6' : '#6b5b73', // Warm beige/lavender
                light: mode === 'dark' ? '#f5f0e6' : '#9e8ba3',
                dark: mode === 'dark' ? '#d4c4a0' : '#4a3f54',
                contrastText: mode === 'dark' ? '#2c2c2c' : '#ffffff',
            },
            background: {
                default: mode === 'dark' ? '#1a1a1a' : '#faf9f7',
                paper: mode === 'dark' ? '#2c2c2c' : '#ffffff',
            },
            text: {
                primary: mode === 'dark' ? '#e8dcc6' : '#2c2c2c',
                secondary: mode === 'dark' ? '#c4b998' : '#5a5a5a',
            },
            divider: mode === 'dark' ? 'rgba(232, 220, 198, 0.12)' : 'rgba(44, 44, 44, 0.12)',
        },
        typography: {
            fontFamily: '"Butler", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: 14, // Base font size
            h1: {
                fontWeight: 700,
                letterSpacing: '0.02em',
                fontSize: '2.5rem',
                '@media (max-width:600px)': {
                    fontSize: '1.75rem',
                },
            },
            h2: {
                fontWeight: 600,
                letterSpacing: '0.01em',
                fontSize: '2rem',
                '@media (max-width:600px)': {
                    fontSize: '1.5rem',
                },
            },
            h3: {
                fontWeight: 600,
                fontSize: '1.75rem',
                '@media (max-width:600px)': {
                    fontSize: '1.25rem',
                },
            },
            h4: {
                fontWeight: 500,
                fontSize: '1.5rem',
                '@media (max-width:600px)': {
                    fontSize: '1.1rem',
                },
            },
            h5: {
                fontWeight: 600,
                fontSize: '1.25rem',
                '@media (max-width:600px)': {
                    fontSize: '1rem',
                },
            },
            h6: {
                fontWeight: 600,
                fontSize: '1rem',
                '@media (max-width:600px)': {
                    fontSize: '0.9rem',
                },
            },
            body1: {
                fontSize: '1rem',
                lineHeight: 1.6,
                '@media (max-width:600px)': {
                    fontSize: '0.9rem',
                },
            },
            body2: {
                fontSize: '0.875rem',
                lineHeight: 1.5,
                '@media (max-width:600px)': {
                    fontSize: '0.8rem',
                },
            },
            button: {
                fontWeight: 600,
                letterSpacing: '0.05em',
                textTransform: 'none' as const,
                fontSize: '1rem',
                '@media (max-width:600px)': {
                    fontSize: '0.9rem',
                },
            },
            caption: {
                fontStyle: 'italic',
                fontSize: '0.85rem',
                lineHeight: 1.4,
                '@media (max-width:600px)': {
                    fontSize: '0.75rem',
                },
            },
            overline: {
                fontSize: '0.875rem',
                fontWeight: 500,
                letterSpacing: '0.02em',
                textTransform: 'none' as const,
                '@media (max-width:600px)': {
                    fontSize: '0.8rem',
                },
            },
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    '@global': {
                        '.elegant-italic': {
                            fontFamily: '"Cormorant Garamond", serif !important',
                            fontStyle: 'italic',
                            fontWeight: 400,
                            letterSpacing: '0.02em',
                        },
                        '.script-text': {
                            fontFamily: '"Cormorant Garamond", cursive !important',
                            fontWeight: 500,
                            letterSpacing: '0.01em',
                        },
                        '.serif-italic': {
                            fontFamily: '"Cormorant Garamond", serif !important',
                            fontStyle: 'italic',
                            fontWeight: 400,
                        },
                    },
                    body: {
                        scrollbarColor: '#6b6b6b #2b2b2b',
                        '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
                            backgroundColor: mode === 'dark' ? '#2b2b2b' : '#f1f1f1',
                        },
                        '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
                            borderRadius: 8,
                            backgroundColor: mode === 'dark' ? '#6b6b6b' : '#c1c1c1',
                            minHeight: 24,
                        },
                        '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
                            backgroundColor: mode === 'dark' ? '#959595' : '#a8a8a8',
                        },
                        '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
                            backgroundColor: mode === 'dark' ? '#959595' : '#a8a8a8',
                        },
                        '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
                            backgroundColor: mode === 'dark' ? '#959595' : '#a8a8a8',
                        },
                    },
                },
            },
        },
    });

    const contextValue: ColorModeContextType = {
        toggleColorMode,
        mode,
    };

    return (
        <ColorModeContext.Provider value={contextValue}>
            <ThemeProvider theme={theme}>
                {children}
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
};